import React from "react";
import { wormColors } from "core/src/core/constants.js";
import styles from "./DuelFightCard.module.scss";

// The ranked 1v1's round over (design E, "Fight card", from /debug/duel-round-win):
// the board dims, the score slams in big, the round winner's digit knocks up
// to its new number with a shake, their name under a line in their colour.
// Match point, the decider and the match itself get a gold stamp. It leaves
// on the same beat the RoundOverCard strip does, a moment before
// the next round takes the board.

// Leave as RoundOverCard's strip does: a 0.4s fade, done 0.2s before the
// next round takes the board.
function leaveTiming(totalSeconds: number) {
  return { "--strip-out-at": `${Math.max(0, totalSeconds - 0.6)}s`, "--strip-out-dur": "0.4s" } as React.CSSProperties;
}

export interface FightSide { id: string; name: string; colorId: string; before: number; after: number }

interface Props {
  left: FightSide;     // you (or the first seat, spectating)
  right: FightSide;
  round: number;       // the round just played
  maxScore: number;    // first to (3)
  totalSeconds: number;
  paused?: boolean;
}

function tagFor(w: number, l: number, max: number): string | null {
  if (w >= max) return "Wins the match";
  if (w === max - 1 && l === max - 1) return "Decider next";
  if (w === max - 1) return "Match point";
  return null;
}

function Digit({ from, to, slam, color }: { from: number; to: number; slam: boolean; color: string }) {
  return (
    <span className={`${styles.digit} ${slam ? styles.slam : ""}`} style={{ color }}>
      {slam ? (
        <>
          <span className={styles.old}>{from}</span>
          <span className={styles.fresh}>{to}</span>
        </>
      ) : to}
    </span>
  );
}

export default function DuelFightCard({ left, right, round, maxScore, totalSeconds, paused }: Props) {
  const lGain = left.after - left.before;
  const rGain = right.after - right.before;
  // the round's winner: whoever's score went up (nobody, for a round that
  // paid no one)
  const winner = lGain > rGain ? left : rGain > lGain ? right : null;
  const loser = winner === left ? right : left;
  const tag = winner ? tagFor(winner.after, loser.after, maxScore) : null;
  const lColor = wormColors[left.colorId] || "#03A9F4";
  const rColor = wormColors[right.colorId] || "#E91E63";
  const wColor = winner ? wormColors[winner.colorId] || "#fff" : "#fff";
  return (
    <div className={`${styles.card} ${paused ? styles.paused : ""}`} style={leaveTiming(totalSeconds)} role="status" aria-live="polite">
      <div className={styles.dim} />
      <div className={styles.center}>
        <div className={styles.round}>Round {round}</div>
        <div className={styles.score} aria-label={`${left.name} ${left.after}, ${right.name} ${right.after}`}>
          <Digit from={left.before} to={left.after} slam={winner === left} color={lColor} />
          <Digit from={right.before} to={right.after} slam={winner === right} color={rColor} />
        </div>
        {winner ? (
          <>
            <div className={styles.under} style={{ "--c": wColor } as React.CSSProperties} />
            <div className={styles.who} style={{ color: wColor }}>{winner.name}</div>
          </>
        ) : (
          <div className={styles.who}>No point this round</div>
        )}
        {tag && <div className={styles.stamp}>{tag}</div>}
      </div>
    </div>
  );
}
